import { useEffect, useState, useRef, Dispatch, SetStateAction } from "react"

interface StickyData<T> {
	value: T
	version: string
}

function hashValue<T>(val: T) {
	return typeof val === "string" ? val : JSON.stringify(val)
}

export function useStickyState<T>(
	initialValue: T,
	key: string,
	debug = false
): [T, Dispatch<SetStateAction<T>>] {
	const [state, setState] = useState<T>(() => {
		if (typeof window === "undefined") return initialValue

		try {
			const stored = localStorage.getItem(key)
			if (!stored) return initialValue
			const parsed: StickyData<T> = JSON.parse(stored)

			const currentVersion = hashValue(initialValue)
			if (parsed.version === currentVersion) {
				if (debug) console.log(`[useStickyState:${key}] restore match`)
				return parsed.value
			} else {
				if (debug) console.warn(`[useStickyState:${key}] restore mismatch → reset`)
				return initialValue
			}
		} catch (err) {
			console.error(`[useStickyState] parse failed for ${key}`, err)
			return initialValue
		}
	})

	// track last known server hash
	const lastServerHash = useRef(hashValue(initialValue))

	// only update when true backend change occurs
	useEffect(() => {
		const newHash = hashValue(initialValue)
		if (newHash !== lastServerHash.current) {
			if (debug)
				console.log(`[useStickyState:${key}] detected backend change`, {
					old: lastServerHash.current,
					new: newHash,
				})
			lastServerHash.current = newHash
			setState(initialValue)
		}
	}, [initialValue, key])

	// persist user edits
	useEffect(() => {
		try {
			const version = lastServerHash.current
			localStorage.setItem(key, JSON.stringify({ value: state, version }))
			if (debug)
				console.log(`[useStickyState:${key}] saved`, {
					value: state,
					version,
				})
		} catch (err) {
			console.error(`[useStickyState] save failed for ${key}`, err)
		}
	}, [state, key])

	return [state, setState]
}

